<script lang="ts">
	import { run } from 'svelte/legacy';

	import { P, Spinner, Badge } from 'flowbite-svelte';
	import AbstractCard from '../components/AbstractCard.svelte';
	import { onMount, onDestroy } from 'svelte';
	import PageHeader from '../components/PageHeader.svelte';
	import { LicenseSummaryCards, RoutedLink, UserBadge, SessionMap } from '../components';
	import { tokenData, isAdmin } from '../Auth';

	import {
		listUsers,
		listAuditLogs,
		listApplications,
		listRoles,
		getDashboardStats,
		getSessionLocationAnalytics,
		getProfile,
		listNotifications,
		getNewsFeed,
		dismissDashboardErrors
	} from '../AppNetwork';
	import type {
		AuditLog,
		AuditUserRef,
		DashboardStats,
		NewsFeedItem,
		SessionLocationBucket,
		User,
		UserNotification
	} from '../AppTypes';
	import site from '../site';
	import { t, t2 } from '../i18n';
	import {
		Chart,
		BarController,
		LineController,
		DoughnutController,
		CategoryScale,
		LinearScale,
		BarElement,
		LineElement,
		PointElement,
		ArcElement,
		Tooltip,
		Legend,
		Filler
	} from 'chart.js';

	Chart.register(
		BarController,
		LineController,
		DoughnutController,
		CategoryScale,
		LinearScale,
		BarElement,
		LineElement,
		PointElement,
		ArcElement,
		Tooltip,
		Legend,
		Filler
	);

	let firstName = $derived($tokenData?.firstName || 'User');

	let userCount = $state(0);
	let auditCount = $state(0);
	let recentLogins = $state(0);
	let appCount = $state(0);
	let roleCount = $state(0);
	let recentActivity: AuditLog[] = $state([]);
	let auditUsers: Record<number, AuditUserRef> = $state({});
	let loading = $state(true);

	/** Non-admin dashboard state. */
	let profile: User | null = $state(null);
	let recentNotifications: UserNotification[] = $state([]);
	let newsFeedItems: NewsFeedItem[] = $state([]);

	let stats: DashboardStats | null = $state(null);
	let locationBuckets: SessionLocationBucket[] = $state([]);
	let locationTotalSessions = $state(0);
	let selectedTimeWindow = $state('30d');

	/** Canvas element references for charts. */
	let signupMonthCanvas: HTMLCanvasElement = $state(undefined as unknown as HTMLCanvasElement);
	let signupWeekCanvas: HTMLCanvasElement = $state(undefined as unknown as HTMLCanvasElement);
	let emailMonthCanvas: HTMLCanvasElement = $state(undefined as unknown as HTMLCanvasElement);
	let appUsageCanvas: HTMLCanvasElement = $state(undefined as unknown as HTMLCanvasElement);

	/** Active chart instances for cleanup. */
	let charts: Chart[] = [];

	const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

	/** Builds a 12-month array from bucketed data. */
	function fillMonthly(buckets: { date: string; count: number }[]): number[] {
		const year = new Date().getFullYear();
		const data = new Array(12).fill(0);
		for (const b of buckets) {
			const month = parseInt(b.date.split('-')[1]) - 1;
			if (b.date.startsWith(String(year))) data[month] = b.count;
		}
		return data;
	}

	/** Builds a 7-day (Sun–Sat) array from bucketed data. */
	function fillWeekly(buckets: { date: string; count: number }[]): number[] {
		const data = new Array(7).fill(0);
		for (const b of buckets) {
			const dayIdx = new Date(b.date + 'T00:00:00').getDay();
			data[dayIdx] = b.count;
		}
		return data;
	}

	/** Initialises chart.js instances for all chart canvases. */
	function buildCharts(): void {
		if (!stats) return;

		/* Signup trend — bar chart by month */
		charts.push(
			new Chart(signupMonthCanvas, {
				type: 'bar',
				data: {
					labels: MONTH_LABELS,
					datasets: [
						{
							label: 'Signups',
							data: fillMonthly(stats.signupsByMonth),
							backgroundColor: 'rgba(59,130,246,0.6)',
							borderRadius: 4
						}
					]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: { legend: { display: false } },
					scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
				}
			})
		);

		/* Signup trend — line chart this week */
		charts.push(
			new Chart(signupWeekCanvas, {
				type: 'line',
				data: {
					labels: DAY_LABELS,
					datasets: [
						{
							label: 'Signups',
							data: fillWeekly(stats.signupsByDay),
							borderColor: 'rgb(16,185,129)',
							backgroundColor: 'rgba(16,185,129,0.1)',
							fill: true,
							tension: 0.3,
							pointRadius: 4
						}
					]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: { legend: { display: false } },
					scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
				}
			})
		);

		/* Emails sent by month */
		charts.push(
			new Chart(emailMonthCanvas, {
				type: 'bar',
				data: {
					labels: MONTH_LABELS,
					datasets: [
						{
							label: 'Emails Sent',
							data: fillMonthly(stats.emailsByMonth),
							backgroundColor: 'rgba(168,85,247,0.6)',
							borderRadius: 4
						}
					]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: { legend: { display: false } },
					scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
				}
			})
		);

		/* Application usage — horizontal bar */
		const appLabels = stats.appUsage.map((a) => a.applicationName);
		const appCounts = stats.appUsage.map((a) => a.userCount);
		const palette = [
			'rgba(59,130,246,0.7)',
			'rgba(16,185,129,0.7)',
			'rgba(168,85,247,0.7)',
			'rgba(239,68,68,0.7)',
			'rgba(245,158,11,0.7)',
			'rgba(236,72,153,0.7)',
			'rgba(99,102,241,0.7)',
			'rgba(20,184,166,0.7)',
			'rgba(249,115,22,0.7)'
		];

		/** Render the application usage chart only when there are registered apps. */
		if (appLabels.length > 0) {
			charts.push(
				new Chart(appUsageCanvas, {
					type: 'bar',
					data: {
						labels: appLabels,
						datasets: [
							{
								label: 'Users',
								data: appCounts,
								backgroundColor: appLabels.map((_, i) => palette[i % palette.length]),
								borderRadius: 4
							}
						]
					},
					options: {
						indexAxis: 'y',
						responsive: true,
						maintainAspectRatio: false,
						plugins: { legend: { display: false } },
						scales: { x: { beginAtZero: true, ticks: { precision: 0 } } }
					}
				})
			);
		}
	}

	onMount(async () => {
		try {
			if ($isAdmin) {
				/** Load admin dashboard data only when the user has admin access. */
				const [usersResult, auditResult, apps, roles, dashStats, locationData, feed] = await Promise.all([
					listUsers({ limit: 1 }),
					listAuditLogs({ limit: 50 }),
					listApplications(),
					listRoles(),
					getDashboardStats(),
					getSessionLocationAnalytics(selectedTimeWindow),
					getNewsFeed().catch(() => [] as NewsFeedItem[])
				]);
				userCount = usersResult.total;
				auditCount = auditResult.total;
				auditUsers = auditResult.users || {};
				appCount = apps.length;
				roleCount = roles.length;
				stats = dashStats;
				locationBuckets = locationData.buckets;
				locationTotalSessions = locationData.totalSessions;
				recentActivity = auditResult.items.slice(0, 8);
				newsFeedItems = feed;

				const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
				recentLogins = auditResult.items.filter(
					(e) => e.action === 'USER_LOGIN' && new Date(e.created) > oneDayAgo
				).length;
			} else {
				/** Load non-admin dashboard data in parallel. */
				const [p, notifs, feed] = await Promise.all([
					getProfile(),
					listNotifications({ limit: 5 }),
					getNewsFeed().catch(() => [] as NewsFeedItem[])
				]);
				profile = p;
				recentNotifications = notifs.items.filter((n) => !n.isRead);
				newsFeedItems = feed;
			}
		} catch (err) {
			console.error('Dashboard stats error:', err);
		} finally {
			loading = false;
		}
	});

	/** Once loading completes and canvases are rendered, build charts. */
	run(() => {
		if (!loading && stats && signupMonthCanvas) {
			buildCharts();
		}
	});

	onDestroy(() => {
		charts.forEach((c) => c.destroy());
		charts = [];
	});

	/** Format a date string as relative time. */
	function formatRelativeTime(dateString: string): string {
		const diff = Date.now() - new Date(dateString).getTime();
		const mins = Math.floor(diff / 60000);
		const hrs = Math.floor(diff / 3600000);
		const days = Math.floor(diff / 86400000);
		/** Return "just now" for very recent timestamps. */
		if (mins < 1) return 'just now';
		/** Return minutes for sub-hour intervals. */
		if (mins < 60) return `${mins}m ago`;
		/** Return hours for sub-day intervals. */
		if (hrs < 24) return `${hrs}h ago`;
		/** Return days for intervals under a week. */
		if (days < 7) return `${days}d ago`;
		return new Date(dateString).toLocaleDateString();
	}

	/** Returns a time-of-day greeting string. */
	function getGreeting(): string {
		const hour = new Date().getHours();
		if (hour < 12) return t2('Good morning');
		if (hour < 18) return t2('Good afternoon');
		return t2('Good evening');
	}

	/** Strip HTML tags from a string (for WP excerpt cleanup). */
	function stripHTML(html: string): string {
		const div = document.createElement('div');
		div.innerHTML = html;
		return div.textContent || '';
	}

	/** Quick-link tiles for the non-admin dashboard. */
	const quickLinks = [
		{
			to: '/Settings/Account',
			icon: 'fa-user',
			color: 'text-primary-500',
			label: t2('My Profile'),
			desc: t2('Edit your account details')
		},
		{
			to: '/Settings/Account/Applications',
			icon: 'fa-cubes',
			color: 'text-indigo-500',
			label: t2('My Applications'),
			desc: t2('Apps you\u2019re registered with')
		},
		{
			to: '/Settings/Account/Sessions',
			icon: 'fa-desktop',
			color: 'text-teal-500',
			label: t2('Sessions'),
			desc: t2('Manage active sessions')
		},
		{
			to: '/Settings/Account/Passkeys',
			icon: 'fa-fingerprint',
			color: 'text-purple-500',
			label: t2('Passkeys'),
			desc: t2('Manage passkey credentials')
		},
		{
			to: '/Settings/Account/Notifications',
			icon: 'fa-bell',
			color: 'text-pink-500',
			label: t2('Notifications'),
			desc: t2('View your notifications')
		}
	];

	/** Dismiss the system error alert and clear it from the dashboard. */
	async function handleDismissErrors(): Promise<void> {
		try {
			await dismissDashboardErrors();
			if (stats) stats.recentErrors = [];
		} catch (err) {
			console.error('Failed to dismiss errors:', err);
		}
	}

	/** Reload session location data when the time window changes. */
	async function changeTimeWindow(window: string): Promise<void> {
		selectedTimeWindow = window;
		/** Fetch updated session location data for the selected time window. */
		try {
			const data = await getSessionLocationAnalytics(window);
			locationBuckets = data.buckets;
			locationTotalSessions = data.totalSessions;
		} catch (err) {
			console.error('Failed to load session locations:', err);
		}
	}
</script>

<PageHeader title={$t("Dashboard")} />

<div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
	{#if $isAdmin}
		<!-- Admin Welcome Card -->
		<AbstractCard class="col-span-full max-w-none">
			<h2 class="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
				{$t("Welcome back, {{name}}!", { name: firstName })}
			</h2>
			<P class="text-gray-500 dark:text-gray-400">
				{$t("This is your {{name}} dashboard. Use the sidebar to navigate through the application.", { name: site.name })}
			</P>
		</AbstractCard>

		<!-- System Errors Alert -->
		{#if stats?.recentErrors && stats.recentErrors.length > 0}
			<div
				class="col-span-full rounded-xl border border-red-300 bg-red-50 p-5 dark:border-red-800 dark:bg-red-950/40"
			>
				<div class="mb-3 flex items-center justify-between">
					<div class="flex items-center gap-2">
						<i class="fa-solid fa-triangle-exclamation text-lg text-red-600 dark:text-red-400"></i>
						<h3 class="font-semibold text-red-800 dark:text-red-300">{$t("System Errors")}</h3>
						<Badge color="red">{stats.recentErrors.length}</Badge>
					</div>
					<button
						onclick={handleDismissErrors}
						class="rounded px-3 py-1 text-xs font-medium text-red-600 transition hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
					>
						<i class="fa-solid fa-check me-1"></i>{$t("Dismiss")}
					</button>
				</div>
				<div class="space-y-2">
					{#each stats.recentErrors as err (err.id)}
						<div
							class="rounded-lg border border-red-200 bg-white p-3 dark:border-red-900 dark:bg-gray-900"
						>
							<div class="flex items-start justify-between gap-4">
								<div class="min-w-0 flex-1">
									<div class="flex items-center gap-2">
										<code
											class="text-xs font-semibold text-red-700 dark:text-red-400"
											>{err.details?.method} {err.details?.url}</code
										>
										<span
											class="text-xs whitespace-nowrap text-gray-400 dark:text-gray-500"
											>{formatRelativeTime(err.created)}</span
										>
									</div>
									<p class="mt-1 text-sm text-gray-700 dark:text-gray-300">
										{err.details?.message}
									</p>
									{#if err.details?.stack}
										<details class="mt-2">
											<summary
												class="cursor-pointer text-xs text-red-600 hover:underline dark:text-red-400"
												>{$t("Stack trace")}</summary
											>
											<pre
												class="bg-depth mt-1 max-h-40 overflow-auto rounded p-2 text-xs text-gray-700 dark:text-gray-300"
												>{err.details.stack}</pre>
										</details>
									{/if}
								</div>
							</div>
						</div>
					{/each}
				</div>
				<RoutedLink to="/Settings/Admin/Logs">
					<span class="mt-3 inline-block text-sm text-red-600 hover:underline dark:text-red-400"
						>{$t("View all audit logs")} &rarr;</span
					>
				</RoutedLink>
			</div>
		{/if}

		<!-- Quick Stats -->
		<AbstractCard class="max-w-none">
			<div class="flex items-center gap-4">
				<div class="rounded-lg bg-green-100 p-3 dark:bg-green-900/30">
					<i class="fa-solid fa-users text-2xl text-green-600 dark:text-green-400"></i>
				</div>
				<div>
					<P class="text-sm text-gray-500 dark:text-gray-400">{$t("Users")}</P>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}<Spinner size="5" />{:else}{userCount}{/if}
					</p>
				</div>
			</div>
		</AbstractCard>

		<AbstractCard class="max-w-none">
			<div class="flex items-center gap-4">
				<div class="rounded-lg bg-blue-100 p-3 dark:bg-blue-900/30">
					<i class="fa-solid fa-right-to-bracket text-2xl text-blue-600 dark:text-blue-400"></i>
				</div>
				<div>
					<P class="text-sm text-gray-500 dark:text-gray-400">{$t("Logins (24h)")}</P>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}<Spinner size="5" />{:else}{recentLogins}{/if}
					</p>
				</div>
			</div>
		</AbstractCard>

		<AbstractCard class="max-w-none">
			<div class="flex items-center gap-4">
				<div class="rounded-lg bg-purple-100 p-3 dark:bg-purple-900/30">
					<i class="fa-solid fa-clipboard-list text-2xl text-purple-600 dark:text-purple-400"></i>
				</div>
				<div>
					<P class="text-sm text-gray-500 dark:text-gray-400">{$t("Audit Entries")}</P>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}<Spinner size="5" />{:else}{auditCount}{/if}
					</p>
				</div>
			</div>
		</AbstractCard>

		<AbstractCard class="max-w-none">
			<div class="flex items-center gap-4">
				<div class="rounded-lg bg-indigo-100 p-3 dark:bg-indigo-900/30">
					<i class="fa-solid fa-cubes text-2xl text-indigo-600 dark:text-indigo-400"></i>
				</div>
				<div>
					<P class="text-sm text-gray-500 dark:text-gray-400">{$t("Applications")}</P>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}<Spinner size="5" />{:else}{appCount}{/if}
					</p>
				</div>
			</div>
		</AbstractCard>

		<AbstractCard class="max-w-none">
			<div class="flex items-center gap-4">
				<div class="rounded-lg bg-yellow-100 p-3 dark:bg-yellow-900/30">
					<i class="fa-solid fa-shield-halved text-2xl text-yellow-600 dark:text-yellow-400"></i>
				</div>
				<div>
					<P class="text-sm text-gray-500 dark:text-gray-400">{$t("Roles")}</P>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}<Spinner size="5" />{:else}{roleCount}{/if}
					</p>
				</div>
			</div>
		</AbstractCard>

		{#if stats}
			<AbstractCard class="max-w-none">
				<div class="flex items-center gap-4">
					<div class="rounded-lg bg-red-100 p-3 dark:bg-red-900/30">
						<i class="fa-solid fa-user-shield text-2xl text-red-600 dark:text-red-400"></i>
					</div>
					<div>
						<P class="text-sm text-gray-500 dark:text-gray-400">{$t("Admins")}</P>
						<p class="text-2xl font-bold text-gray-900 dark:text-white">{stats.adminCount}</p>
					</div>
				</div>
			</AbstractCard>
			<AbstractCard class="max-w-none">
				<div class="flex items-center gap-4">
					<div class="rounded-lg bg-pink-100 p-3 dark:bg-pink-900/30">
						<i class="fa-solid fa-envelope text-2xl text-pink-600 dark:text-pink-400"></i>
					</div>
					<div>
						<P class="text-sm text-gray-500 dark:text-gray-400">{$t("Emails Sent")}</P>
						<p class="text-2xl font-bold text-gray-900 dark:text-white">
							{stats.totalEmailsSent.toLocaleString()}
						</p>
					</div>
				</div>
			</AbstractCard>
			<AbstractCard class="max-w-none">
				<div class="flex items-center gap-4">
					<div class="rounded-lg bg-teal-100 p-3 dark:bg-teal-900/30">
						<i class="fa-solid fa-paper-plane text-2xl text-teal-600 dark:text-teal-400"></i>
					</div>
					<div>
						<P class="text-sm text-gray-500 dark:text-gray-400">{$t("Emails This Week")}</P>
						<p class="text-2xl font-bold text-gray-900 dark:text-white">
							{stats.emailsByDay.reduce((sum, b) => sum + b.count, 0).toLocaleString()}
						</p>
					</div>
				</div>
			</AbstractCard>
			<AbstractCard class="max-w-none">
				<div class="flex items-center gap-4">
					<div class="rounded-lg bg-orange-100 p-3 dark:bg-orange-900/30">
						<i class="fa-solid fa-calendar-days text-2xl text-orange-600 dark:text-orange-400"></i>
					</div>
					<div>
						<P class="text-sm text-gray-500 dark:text-gray-400">{$t("Emails This Month")}</P>
						<p class="text-2xl font-bold text-gray-900 dark:text-white">
							{(
								stats.emailsByMonth.find((b) => {
									const now = new Date();
									return (
										b.date === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
									);
								})?.count ?? 0
							).toLocaleString()}
						</p>
					</div>
				</div>
			</AbstractCard>
		{/if}
	{/if}
</div>

{#if $isAdmin && stats}
	<!-- Session Location Map -->
	<AbstractCard class="mt-6 max-w-none">
		<div class="mb-4 flex items-center justify-between">
			<h3 class="text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-globe mr-2 text-blue-500"></i>{$t("Session Locations")}
			</h3>
			<div class="flex gap-1">
				{#each [{ value: '24h', label: '24h' }, { value: '7d', label: '7d' }, { value: '30d', label: '30d' }, { value: 'all', label: 'All' }] as tw (tw.value)}
					<button
						class="rounded-lg px-3 py-1 text-xs font-medium transition-colors {selectedTimeWindow ===
						tw.value
							? 'bg-primary-600 text-white'
							: 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'}"
						onclick={() => changeTimeWindow(tw.value)}
					>
						{tw.label}
					</button>
				{/each}
			</div>
		</div>
		<SessionMap buckets={locationBuckets} totalSessions={locationTotalSessions} />
	</AbstractCard>

	<!-- Charts Grid -->
	<div class="mt-6 grid gap-6 md:grid-cols-2">
		<AbstractCard class="max-w-none">
			<h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-chart-column mr-2 text-blue-500"></i>{$t("Signups This Year")}
			</h3>
			<div style="height:260px"><canvas bind:this={signupMonthCanvas}></canvas></div>
		</AbstractCard>
		<AbstractCard class="max-w-none">
			<h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-chart-line mr-2 text-green-500"></i>{$t("Signups This Week")}
			</h3>
			<div style="height:260px"><canvas bind:this={signupWeekCanvas}></canvas></div>
		</AbstractCard>
		<AbstractCard class="max-w-none">
			<h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-chart-column mr-2 text-purple-500"></i>{$t("Emails Sent This Year")}
			</h3>
			<div style="height:260px"><canvas bind:this={emailMonthCanvas}></canvas></div>
		</AbstractCard>
		<AbstractCard class="max-w-none">
			<h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-cubes mr-2 text-indigo-500"></i>{$t("Application Usage")}
			</h3>
			{#if stats.appUsage.length > 0}
				<div style="height:260px"><canvas bind:this={appUsageCanvas}></canvas></div>
			{:else}
				<p class="text-sm text-gray-500 dark:text-gray-400">{$t("No application registrations yet.")}</p>
			{/if}
		</AbstractCard>
	</div>
{/if}

{#if $isAdmin}
	<!-- Quick Actions -->
	<div class="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
		<RoutedLink to="/Users">
			<div
				class="widget bg-depth-hover p-4 text-left transition"
			>
				<i class="fa-solid fa-users text-primary-500 mb-2 text-lg"></i>
				<p class="font-medium text-gray-900 dark:text-white">{$t("Manage Users")}</p>
				<p class="text-xs text-gray-500 dark:text-gray-400">{$t("View and edit accounts")}</p>
			</div>
		</RoutedLink>
		<RoutedLink to="/Applications">
			<div
				class="widget bg-depth-hover p-4 text-left transition"
			>
				<i class="fa-solid fa-cubes mb-2 text-lg text-indigo-500"></i>
				<p class="font-medium text-gray-900 dark:text-white">{$t("Applications")}</p>
				<p class="text-xs text-gray-500 dark:text-gray-400">{$t("Register and manage apps")}</p>
			</div>
		</RoutedLink>
		<RoutedLink to="/Roles">
			<div
				class="widget bg-depth-hover p-4 text-left transition"
			>
				<i class="fa-solid fa-shield-halved mb-2 text-lg text-yellow-500"></i>
				<p class="font-medium text-gray-900 dark:text-white">{$t("Roles")}</p>
				<p class="text-xs text-gray-500 dark:text-gray-400">{$t("Define access roles")}</p>
			</div>
		</RoutedLink>
		<RoutedLink to="/Settings/Admin">
			<div
				class="widget bg-depth-hover p-4 text-left transition"
			>
				<i class="fa-solid fa-cog mb-2 text-lg text-pink-500"></i>
				<p class="font-medium text-gray-900 dark:text-white">{$t("Settings")}</p>
				<p class="text-xs text-gray-500 dark:text-gray-400">{$t("Platform configuration")}</p>
			</div>
		</RoutedLink>
	</div>

	<!-- Admin: their own licences, same block the non-admin home shows. -->
	<LicenseSummaryCards />

	<!-- Recent Activity -->
	{#if recentActivity.length > 0}
		<AbstractCard class="mt-6 max-w-none">
			<h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-clock-rotate-left mr-2"></i>
				{$t("Recent Activity")}
			</h3>
			<div class="space-y-3">
				{#each recentActivity as log (log.id)}
					<div
						class="flex items-center justify-between border-b border-gray-100 py-2 last:border-0 dark:border-gray-700"
					>
						<div class="flex items-center gap-3">
							<Badge
								color={log.action.includes('DELETE') || log.action.includes('DEACTIVAT') || log.action.includes('ERROR')
									? 'red'
									: log.action.includes('CREATE') ||
										  log.action.includes('REGISTER') ||
										  log.action.includes('ENABLED') ||
										  log.action.includes('VERIFIED') ||
										  log.action.includes('ACTIVATED')
										? 'green'
										: 'blue'}
							>
								{log.action.replace(/_/g, ' ')}
							</Badge>
							{#if log.userId && auditUsers[log.userId]}
								<span class="text-sm text-gray-500 dark:text-gray-400">{$t("by")}</span>
								<UserBadge
									userId={log.userId}
									firstName={auditUsers[log.userId].firstName}
									lastName={auditUsers[log.userId].lastName}
									email={auditUsers[log.userId].email}
									size="xs"
								/>
							{:else if log.userId}
								<span class="text-sm text-gray-500 dark:text-gray-400">{$t("by User #{{id}}", { id: log.userId })}</span>
							{/if}
						</div>
						<span class="text-xs whitespace-nowrap text-gray-400 dark:text-gray-500"
							>{formatRelativeTime(log.created)}</span
						>
					</div>
				{/each}
			</div>
			<RoutedLink to="/Settings/Admin/Logs">
				<span class="text-primary-600 dark:text-primary-400 mt-4 text-sm hover:underline"
					>{$t("View all audit logs")} &rarr;</span
				>
			</RoutedLink>
		</AbstractCard>
	{/if}

	<!-- Admin: News Feed -->
	{#if newsFeedItems.length > 0}
		<AbstractCard class="mt-6 max-w-none">
			<h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-newspaper mr-2 text-red-500"></i>{$t("News Feed")}
			</h3>
			<div class="space-y-3">
				{#each newsFeedItems as item (item.id)}
					<a
						href={item.link}
						target="_blank"
						rel="noopener noreferrer"
						class="group flex gap-4 overflow-hidden rounded-lg border border-transparent p-3 transition hover:border-gray-200 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-700/50"
					>
						{#if item.image}
							<img
								src={item.image}
								alt=""
								class="h-16 w-24 flex-none rounded-lg object-cover"
								loading="lazy"
							/>
						{:else}
							<div
								class="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400"
							>
								<i class="fa-solid fa-newspaper"></i>
							</div>
						{/if}
						<div class="min-w-0 flex-1">
							<p
								class="font-medium text-gray-900 group-hover:text-red-600 dark:text-white dark:group-hover:text-red-400"
							>
								{item.title}
							</p>
							<p class="mt-0.5 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
								{stripHTML(item.excerpt)}
							</p>
							<p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
								{new Date(item.date).toLocaleDateString(undefined, {
									year: 'numeric',
									month: 'short',
									day: 'numeric'
								})}
							</p>
						</div>
						<i
							class="fa-solid fa-arrow-up-right-from-square mt-1 text-xs text-gray-300 transition group-hover:text-red-400 dark:text-gray-600"
						></i>
					</a>
				{/each}
			</div>
		</AbstractCard>
	{/if}
{:else}
	<!-- Non-admin: Hero Banner -->
	<div
		class="mt-6 overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8 shadow-lg dark:border-gray-600 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800"
	>
		<div class="mb-3 flex items-center gap-2">
			<span class="inline-block h-0.5 w-6 bg-red-500"></span>
			<span class="text-xs font-semibold tracking-widest text-red-400 uppercase">{$t("Your Account")}</span>
		</div>
		<h2 class="text-2xl font-bold text-white sm:text-3xl">
			{getGreeting()}, {firstName}.
		</h2>
		<p class="mt-2 max-w-lg text-sm text-gray-400">
			{$t("Your gateway to the Abstract platform. Manage your identity, applications, and security — all in one place.")}
		</p>
	</div>

	<!-- Non-admin: Security Posture Tiles -->
	{#if profile}
		<div class="mt-4 flex flex-wrap gap-3">
			<div
				class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-depth px-4 py-2 dark:border-gray-700"
			>
				<i
					class="fa-solid {profile.isVerified
						? 'fa-circle-check text-green-500'
						: 'fa-circle-xmark text-red-500'} text-sm"
				></i>
				<span class="text-xs font-medium text-gray-600 dark:text-gray-300">
					{profile.isVerified ? $t('Email Verified') : $t('Email Unverified')}
				</span>
			</div>
			<div
				class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-depth px-4 py-2 dark:border-gray-700"
			>
				<i
					class="fa-solid {profile.twoFactorEnabled
						? 'fa-shield-halved text-green-500'
						: 'fa-triangle-exclamation text-yellow-500'} text-sm"
				></i>
				<span class="text-xs font-medium text-gray-600 dark:text-gray-300">
					{profile.twoFactorEnabled ? $t('2FA Enabled') : $t('2FA Not Set')}
				</span>
			</div>
			<div
				class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-depth px-4 py-2 dark:border-gray-700"
			>
				<i
					class="fa-solid {profile.passwordless
						? 'fa-envelope text-blue-500'
						: 'fa-lock text-green-500'} text-sm"
				></i>
				<span class="text-xs font-medium text-gray-600 dark:text-gray-300">
					{profile.passwordless ? $t('Passwordless') : $t('Password')}
				</span>
			</div>
		</div>
	{:else if loading}
		<div class="mt-6 flex items-center justify-center py-8">
			<Spinner size="8" />
		</div>
	{/if}

	<!-- Non-admin: Quick Links -->
	<div class="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
		{#each quickLinks as link (link.to)}
			<RoutedLink to={link.to}>
				<div
					class="widget bg-depth-hover group flex items-center gap-4 p-5 text-left transition hover:shadow-md"
				>
					<div
						class="rounded-lg bg-gray-100 p-3 transition group-hover:bg-gray-200 dark:bg-gray-700 dark:group-hover:bg-gray-600"
					>
						<i class="fa-solid {link.icon} text-lg {link.color}"></i>
					</div>
					<div>
						<p class="font-medium text-gray-900 dark:text-white">{link.label}</p>
						<p class="text-xs text-gray-500 dark:text-gray-400">{link.desc}</p>
					</div>
				</div>
			</RoutedLink>
		{/each}
	</div>

	<!-- Non-admin: Your Licenses -->
	<LicenseSummaryCards />

	<!-- Non-admin: News Feed -->
	{#if newsFeedItems.length > 0}
		<div
			class="widget mt-6 p-6 shadow-sm"
		>
			<div class="mb-5 flex items-center gap-2">
				<span class="inline-block h-0.5 w-6 bg-red-500"></span>
				<span class="text-xs font-semibold tracking-widest text-red-500 uppercase dark:text-red-400"
					>{$t("Latest News")}</span
				>
			</div>
			<div class="space-y-4">
				{#each newsFeedItems as item (item.id)}
					<a
						href={item.link}
						target="_blank"
						rel="noopener noreferrer"
						class="group flex gap-4 overflow-hidden rounded-lg border border-transparent p-3 transition hover:border-gray-200 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-700/50"
					>
						{#if item.image}
							<img
								src={item.image}
								alt=""
								class="h-20 w-28 flex-none rounded-lg object-cover sm:h-24 sm:w-36"
								loading="lazy"
							/>
						{:else}
							<div
								class="mt-0.5 flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400"
							>
								<i class="fa-solid fa-newspaper"></i>
							</div>
						{/if}
						<div class="min-w-0 flex-1">
							<p
								class="font-medium text-gray-900 group-hover:text-red-600 dark:text-white dark:group-hover:text-red-400"
							>
								{item.title}
							</p>
							<p class="mt-0.5 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
								{stripHTML(item.excerpt)}
							</p>
							<p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
								{new Date(item.date).toLocaleDateString(undefined, {
									year: 'numeric',
									month: 'short',
									day: 'numeric'
								})}
							</p>
						</div>
						<i
							class="fa-solid fa-arrow-up-right-from-square mt-1 text-xs text-gray-300 transition group-hover:text-red-400 dark:text-gray-600"
						></i>
					</a>
				{/each}
			</div>
		</div>
	{/if}

	<!-- Non-admin: Recent Notifications -->
	{#if recentNotifications.length > 0}
		<div
			class="widget mt-6 p-6 shadow-sm"
		>
			<h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-bell mr-2 text-pink-500"></i>{$t("Recent Notifications")}
			</h3>
			<div class="space-y-3">
				{#each recentNotifications as notif (notif.id)}
					<div
						class="flex items-center justify-between border-b border-gray-100 py-2 last:border-0 dark:border-gray-700"
					>
						<div class="flex items-center gap-3">
							<Badge
								color={notif.notificationType === 'error'
									? 'red'
									: notif.notificationType === 'warning'
										? 'yellow'
										: notif.notificationType === 'success'
											? 'green'
											: 'blue'}
							>
								{notif.notificationType}
							</Badge>
							<div>
								<p class="text-sm font-medium text-gray-900 dark:text-white">{notif.title}</p>
								{#if notif.message}
									<p class="text-xs text-gray-500 dark:text-gray-400">{notif.message}</p>
								{/if}
							</div>
						</div>
						<span class="text-xs whitespace-nowrap text-gray-400 dark:text-gray-500"
							>{formatRelativeTime(notif.created)}</span
						>
					</div>
				{/each}
			</div>
			<RoutedLink to="/Settings/Account/Notifications">
				<span class="text-primary-600 dark:text-primary-400 mt-4 text-sm hover:underline"
					>{$t("View all notifications")} &rarr;</span
				>
			</RoutedLink>
		</div>
	{/if}
{/if}
